import { defineStore } from "pinia";
import fightsApi, { FightsQueuesResult, TimeCalculatingFightsCollection } from "../Api/fights";
import objectMatcher from "../Common/objectMatcher";
import sessionStore from "../Common/sessionStore";
import { CalledCompetitor, Mat } from "../Common/types";
import { ComplexFight, FightQueueStatus } from "../Common/Fights/types";
import competitorCallHistoryApi, { CompetitorCallHistory } from "../Api/competitorCallHistoryApi";
import { WebsocketClientType } from "../Common/websocketClient";
import { getColorSchema } from "../Common/Fights/colorSchema";
import type { ColorSchema } from "../Common/types";
import type { ScoreboardType } from "../Scoreboard/Common/types";

function fightAsString(fight: ComplexFight) {
    const array = [
        fight.category,
        fight.firstCompetitor.firstName,
        fight.firstCompetitor.lastName,
        fight.firstCompetitor.academy,
        fight.secondCompetitor.firstName,
        fight.secondCompetitor.lastName,
        fight.secondCompetitor.academy,
    ];

    return array.join(" ");
}

export function getCalledCompetitorsSessionStoreKey(eventId: number): string {
    return "called_competitors_for_event_" + eventId;
}

export const useAnnouncerBoardStore = defineStore("announcer-board", {
    state: () => {
        return {
            websocketClient: null as WebsocketClientType | null,
            initDone: false,
            //Displaying fights lists
            eventId: 0,
            eventTimezone: null,
            fightsForMat: {} as Record<number, TimeCalculatingFightsCollection>,
            fightQueueStatuses: {} as Record<number, FightQueueStatus>,
            matsForGivenPage: [] as Mat[],

            //Tracking times for each mat: fightId => left sec.
            trackedTimeForFights: {} as Record<number, number>,

            //Options
            matsPerPage: 6,
            searchQuery: "",
            selectedMatIds: [] as number[],
            fightsLimit: 15,
            pageNumber: 0,

            //Timer
            timer: 20,
            isTimerRunning: false,

            //Called competitors
            calledCompetitors: [] as CalledCompetitor[],
            competitorCallHistoryMap: {} as Record<number, CompetitorCallHistory[]>, //fightId => call counts
        };
    },
    getters: {
        filteredFightsForMat: (state): FightsQueuesResult["fightsCollections"] => {
            const result = {};
            for (const matId of Object.keys(state.fightsForMat)) {
                const fightsWithStartTimes = state.fightsForMat[matId];
                result[matId] = fightsWithStartTimes.filter(({ fight }) => {
                    return objectMatcher.matchString(state.searchQuery, fightAsString(fight));
                });
            }
            return result;
        },
        haveFightsForCurrentPage(state): boolean {
            for (const mat of state.matsForGivenPage) {
                if (this.filteredFightsForMat[mat.id].length > 0) {
                    return true;
                }
            }
            return false;
        },
        colorSchemaByFightId(state): Record<number, ColorSchema> {
            const result: Record<number, ColorSchema> = {};
            Object.values(state.fightsForMat).forEach(fightsWithStartTimes => {
                fightsWithStartTimes.forEach(fightWithStartTime => {
                    result[fightWithStartTime.fight.id] = getColorSchema(fightWithStartTime.scoreboardType);
                });
            });
            return result;
        },
        scoreboardTypeByFightId(state): Record<number, ScoreboardType> {
            const result: Record<number, ScoreboardType> = {};
            Object.values(state.fightsForMat).forEach(fightsWithStartTimes => {
                fightsWithStartTimes.forEach(fightWithStartTime => {
                    result[fightWithStartTime.fight.id] = fightWithStartTime.scoreboardType;
                });
            });
            return result;
        },
        matNameById: state => {
            return (matId: number): string => {
                const foundMat = state.matsForGivenPage.find(mat => mat.id === matId);
                return foundMat ? foundMat.name : "";
            };
        },
        sortedCalledCompetitors(state): CalledCompetitor[] {
            return state.calledCompetitors.sort((a, b) => b.time - a.time);
        },
    },
    actions: {
        addCallOfCompetitorForMat(websocketEvent) {
            const existingIndex = this.calledCompetitors.findIndex(c => c.fightId === websocketEvent.fightId);

            if (existingIndex !== -1) {
                this.calledCompetitors[existingIndex] = {
                    ...this.calledCompetitors[existingIndex],
                    calledCompetitorIds: websocketEvent.calledCompetitorIds,
                    time: websocketEvent.time,
                };
            } else {
                this.calledCompetitors.push(websocketEvent);
            }

            sessionStore.set(getCalledCompetitorsSessionStoreKey(this.eventId), JSON.stringify(this.calledCompetitors));
        },
        removeCalledCompetitors(fightId: number) {
            this.calledCompetitors = this.calledCompetitors.filter(calledCompetitor => calledCompetitor.fightId !== fightId);
            sessionStore.set(getCalledCompetitorsSessionStoreKey(this.eventId), JSON.stringify(this.calledCompetitors));
        },
        refreshAllMats(): Promise<void> {
            return fightsApi
                .getFightsQueues(this.eventId, this.selectedMatIds, this.fightsLimit, this.matsPerPage, this.pageNumber)
                .then(response => {
                    this.fightsForMat = response.fightsCollections;
                    this.fightQueueStatuses = response.fightQueueStatuses;
                    this.matsForGivenPage = response.matsForGivenPage;
                });
        },
        /**
         * Usage should handle error.
         */
        refreshCallCountHistory(): Promise<void> {
            return competitorCallHistoryApi.getCallCountHistoryMap(this.eventId).then(map => {
                this.competitorCallHistoryMap = map;
            });
        },
    },
});
